// SEO Utility Functions
export const generatePageTitle = (pageTitle?: string): string => {
  const baseTitle = "ClipThis - AI Video Clipping Tool";
  return pageTitle ? `${pageTitle} | ${baseTitle}` : `${baseTitle} | Turn Long Videos into Viral Clips`;
};

export const generatePageDescription = (pageDescription?: string): string => {
  const baseDescription = "AI-powered video clipping tool that automatically finds highlights in your content. Transform streams, gaming videos, and long content into viral clips in minutes.";
  return pageDescription || baseDescription;
};

export const generateKeywords = (additionalKeywords: string[] = []): string => {
  const baseKeywords = [
    "AI video clipping",
    "automatic video editing", 
    "viral clips",
    "content creation",
    "streaming clips",
    "gaming highlights",
    "video AI",
    "content creator tools",
    "viral video maker",
    "auto clip generator",
    "twitch highlights",
    "youtube shorts",
    "tiktok clips",
    "free video editor",
    "AI video analysis"
  ];
  
  return [...baseKeywords, ...additionalKeywords].join(", ");
};

// Schema.org structured data generators
export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "ClipThis",
  "url": "https://clipthis.io",
  "description": "AI-powered video clipping tool that automatically finds highlights in your content and creates viral clips",
  "potentialAction": {
    "@type": "SearchAction",
          "target": "https://clipthis.io/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

export const generateSoftwareApplicationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "ClipThis",
  "description": "AI-powered video clipping tool that automatically finds highlights in your content and creates viral clips",
  "url": "https://clipthis.io",
  "applicationCategory": "VideoEditingApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "892"
  },
  "creator": {
    "@type": "Organization",
    "name": "ClipThis",
    "url": "https://clipthis.io"
  },
  "featureList": [
    "AI-powered highlight detection",
    "Automatic clip generation", 
    "Multiple export formats",
    "Gaming stream optimization",
    "Social media ready clips"
  ]
});

export const generateBreadcrumbSchema = (breadcrumbs: Array<{name: string, url: string}>) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": breadcrumbs.map((crumb, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": crumb.name,
    "item": crumb.url
  }))
});

// Declare global gtag interface
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Google Analytics setup
export const initializeGA = (measurementId: string) => {
  if (typeof window !== 'undefined') {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    const gtag = (...args: any[]) => {
      window.dataLayer.push(args);
    };
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', measurementId);
  }
};

// Track page views
export const trackPageView = (url: string, title: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'GA_MEASUREMENT_ID', {
      page_title: title,
      page_location: url,
    });
  }
};

// Track events
export const trackEvent = (action: string, category: string, label?: string, value?: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  }
}; 